/* GS://PROTOCOLO
 * Arquivo: src/pages/NFEntryPage.jsx
 * Dossiê de Projeto e Colaboração: Ecossistema GoldenSisters
 * Versão: 3.6 – FIX: Região Cloud Functions (SouthAmerica)
 * Impacto da Versão: [src/pages/NFEntryPage.jsx]
 * Data: 29/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [FIX]: getFunctions agora aponta explicitamente para 'southamerica-east1'.
 * - [UI]: Pequeno ajuste no spinner para feedback visual mais claro.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  FileText,
  Archive,
  Plus,
  Trash2,
  Save,
  AlertCircle,
  Loader2,
  DollarSign
} from 'lucide-react';
import { createNFEntryWithItemsAndLot } from '../services/gs_nf_module_service';
import { ITEM_TYPES } from '../services/gs_nf_module_dataModel';
import {
  getSuppliers,
  addSupplier,
  addAccountPayable,
  payAccount,
  getFinancialCategories
} from '../services/firestoreService';
import productService from '../services/productService';
import SupplierForm from '../components/forms/SupplierForm';
import { Timestamp, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
// [GS-CLOUD]: Imports para chamar a Function
import { getFunctions, httpsCallable } from 'firebase/functions';

// ========================
// Regras de Ouro (Negócio)
// ========================
const MARGEM_ALVO = 0.65; // 65%
const CUSTO_EMBALAGEM_PADRAO = 1.50; // R$ por peça pronta

// ========================
// Utilitários
// ========================
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const formatBRL = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(n) || 0
  );

const DEFAULT_ITEM = (itemType = ITEM_TYPES.BRUTA) => ({
  localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  itemType,
  supplierItemCode: '',
  supplierItemName: '',
  categoryId: '', // Obrigatório para evitar SKU GEN
  generatedSku: '', // [GS-NEW]: SKU vindo da nuvem
  isLoadingSku: false, // [GS-NEW]: Estado de carregamento do SKU
  tags: '',
  quantity: 1,
  unit: 'un',
  rawWeight: 0,
  unitValue: 0,
  suggestedPrice: 0, // Apenas informativo
});

const NFEntryPage = ({ openModal }) => {
  // --- ESTADOS ---
  const [nfHeader, setNfHeader] = useState({
    nfNumber: '',
    supplierName: '',
    supplierId: '',
    nfDate: '',
    notes: '',
    nfType: ITEM_TYPES.BRUTA,
  });

  const [expenses, setExpenses] = useState({
    frete: '',
    pedagio: '',
    combustivel: '',
    alimentacao: '',
    outros: '',
  });

  const [items, setItems] = useState([DEFAULT_ITEM(ITEM_TYPES.BRUTA)]);

  const [financialData, setFinancialData] = useState({
    generateBill: true,
    dueDate: '',
    isPaid: false,
    paymentMethod: 'Pix',
    categoryId: ''
  });

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({
    header: {},
    items: {},
    general: '',
  });

  const [suppliers, setSuppliers] = useState([]);
  const [productCategories, setProductCategories] = useState([]); 
  const [financialCategories, setFinancialCategories] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);

  // --- CARGA DE DADOS ---
  const loadInitialData = useCallback(async () => {
    try {
      setIsLoadingSuppliers(true);
      const [suppliersData, finCatData, prodCatData] = await Promise.all([
        getSuppliers(),
        getFinancialCategories(),
        productService.getAllCategories()
      ]);
      setSuppliers(suppliersData || []);
      setProductCategories(prodCatData || []);

      const expCats = (finCatData || []).filter(c => c.type === 'expense');
      setFinancialCategories(expCats);

      const stockCat = expCats.find(
        c =>
          (c.name || '').toLowerCase().includes('estoque') ||
          (c.name || '').toLowerCase().includes('compra')
      );

      if (stockCat) {
        setFinancialData(prev => ({ ...prev, categoryId: stockCat.id }));
      } else if (expCats.length > 0) {
        setFinancialData(prev => ({ ...prev, categoryId: expCats[0].id }));
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      if (openModal) {
        openModal(
          <p className="text-red-500">Erro ao carregar dados iniciais.</p>,
          'Erro de Carregamento'
        );
      }
    } finally {
      setIsLoadingSuppliers(false);
    }
  }, [openModal]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // =========================
  // Motor de Precificação (Sugestão)
  // =========================
  const calculateSuggestedPrice = useCallback((costUnit) => {
    const divisor = 1 - MARGEM_ALVO; 
    if (divisor <= 0) return 0;
    const baseCost = Number(costUnit) || 0;
    const suggested = (baseCost + CUSTO_EMBALAGEM_PADRAO) / divisor;
    return round2(suggested);
  }, []);

  const recalcItemsPricing = useCallback(
    (itemsDraft = [], nfType, totalExpenses) => {
      // Só calcula sugestão se for PRONTA
      if (nfType !== ITEM_TYPES.PRONTA) return itemsDraft;

      const safeTotalExpenses = Number(totalExpenses) || 0;
      const eligible = itemsDraft.filter(
        (it) => it.itemType === ITEM_TYPES.BRUTA || it.itemType === ITEM_TYPES.PRONTA
      );

      const totalEligibleGross = eligible.reduce((sum, it) => {
        const q = Number(it.quantity) || 0;
        const u = Number(it.unitValue) || 0;
        return sum + q * u;
      }, 0);

      if (!totalEligibleGross || totalEligibleGross <= 0) {
        return itemsDraft.map((it) => {
          const unitValue = Number(it.unitValue) || 0;
          const suggestedPrice = calculateSuggestedPrice(unitValue);
          if (Number(it.suggestedPrice) === suggestedPrice) return it;
          return { ...it, suggestedPrice };
        });
      }

      return itemsDraft.map((it) => {
        const qty = Number(it.quantity) || 0;
        const qtyForDivision = qty > 0 ? qty : 1;
        const gross = (Number(it.unitValue) || 0) * qty;
        const isEligible = it.itemType === ITEM_TYPES.BRUTA || it.itemType === ITEM_TYPES.PRONTA;

        const portion = isEligible ? gross / totalEligibleGross : 0;
        const rateioTotal = safeTotalExpenses > 0 ? portion * safeTotalExpenses : 0;
        const rateioUnit = rateioTotal / qtyForDivision;
        const costUnit = (Number(it.unitValue) || 0) + rateioUnit;

        const suggestedPrice = calculateSuggestedPrice(costUnit);

        if (Number(it.suggestedPrice) === suggestedPrice) return it;
        return { ...it, suggestedPrice };
      });
    },
    [calculateSuggestedPrice]
  );

  // --- HANDLERS ---
  const handleHeaderChange = (field, value) => {
    setNfHeader((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'nfType') {
        setItems((prevItems) => {
          const mapped = prevItems.map((it) => ({ ...it, itemType: value }));
          if (value === ITEM_TYPES.PRONTA) {
            const toNum = (v) => Number(v) || 0;
            const currentTotalExpenses =
              toNum(expenses.frete) + toNum(expenses.pedagio) +
              toNum(expenses.combustivel) + toNum(expenses.alimentacao) +
              toNum(expenses.outros);
            return recalcItemsPricing(mapped, value, currentTotalExpenses);
          }
          return mapped;
        });
      }
      return updated;
    });
  };

  const handleFinancialChange = (field, value) => {
    setFinancialData(prev => ({ ...prev, [field]: value }));
  };

  const handleSupplierSelect = (supplierId) => {
    const selected = suppliers.find((s) => s.id === supplierId) || null;
    setNfHeader((prev) => ({
      ...prev,
      supplierId: supplierId || '',
      supplierName: selected ? selected.name : '',
    }));
  };

  const handleAddNewSupplier = () => {
    if (!openModal) return;
    openModal(
      <SupplierForm
        onClose={() => openModal(null)}
        onSave={async (supplierData) => {
          try {
            const result = await addSupplier(supplierData);
            await loadInitialData();
            let newSupplierId = result?.id || result?.docRef?.id;
            if (newSupplierId) {
              setNfHeader((prev) => ({
                ...prev,
                supplierId: newSupplierId,
                supplierName: supplierData.name || '',
              }));
            }
          } catch (error) {
            console.error('Erro ao cadastrar fornecedor:', error);
          }
          openModal(null);
        }}
      />,
      'Cadastrar Novo Fornecedor', '2xl'
    );
  };

  const handleExpenseChange = (field, value) => {
    setExpenses((prev) => {
      const updated = { ...prev, [field]: value };
      const nfType = nfHeader.nfType || ITEM_TYPES.BRUTA;
      if (nfType === ITEM_TYPES.PRONTA) {
        const toNum = (v) => Number(v) || 0;
        const total =
          toNum(updated.frete) + toNum(updated.pedagio) +
          toNum(updated.combustivel) + toNum(updated.alimentacao) +
          toNum(updated.outros);
        setItems((prevItems) => recalcItemsPricing(prevItems, nfType, total));
      }
      return updated;
    });
  };

  // [GS-CLOUD]: Função para chamar o gerador de SKU
  const generateSkuForItem = async (localId, categoryId, supplierId) => {
    // 1. Validar pré-requisitos
    if (!categoryId || !supplierId) return;

    // Encontrar o código da categoria (assumindo que o objeto categoria tem o campo 'code' ou 'categoryCode')
    const categoryObj = productCategories.find(c => c.id === categoryId);
    // Fallback: Tenta pegar os 3 primeiros caracteres do nome se não tiver code (não ideal, mas previne crash)
    const catCode = categoryObj?.code || categoryObj?.categoryCode || (categoryObj?.name || "XXX").substring(0,3).toUpperCase();

    // Atualizar estado para loading
    setItems(prev => prev.map(it => it.localId === localId ? { ...it, isLoadingSku: true } : it));

    try {
      // [FIX]: Especificando a região explicitamente
      const functions = getFunctions(undefined, 'southamerica-east1');
      const generateProductSku = httpsCallable(functions, 'generateProductSku');
      
      const response = await generateProductSku({ 
        categoryCode: catCode, 
        supplierId: supplierId 
      });

      const newSku = response.data.sku;

      // Atualizar item com o SKU gerado
      setItems(prev => prev.map(it => 
        it.localId === localId ? { ...it, generatedSku: newSku, isLoadingSku: false } : it
      ));

    } catch (error) {
      console.error("Erro ao gerar SKU:", error);
      setItems(prev => prev.map(it => 
        it.localId === localId ? { ...it, isLoadingSku: false } : it
      ));
      // Opcional: Mostrar erro visual
    }
  };

  const handleItemChange = (localId, field, value) => {
    // [GS-CLOUD]: Se mudou a categoria e já temos fornecedor, tentar gerar SKU
    if (field === 'categoryId') {
        // Verifica se já tem SKU gerado para não sobrescrever (a menos que seja desejado resetar)
        // Aqui optamos por gerar se estiver vazio.
        const currentItem = items.find(it => it.localId === localId);
        if (nfHeader.supplierId && !currentItem?.generatedSku) {
            generateSkuForItem(localId, value, nfHeader.supplierId);
        }
    }

    setItems((prev) => {
      const nfType = nfHeader.nfType || ITEM_TYPES.BRUTA;
      const next = prev.map((it) => {
        if (it.localId !== localId) return it;
        if (['quantity', 'unitValue'].includes(field)) {
          return { ...it, [field]: value === '' ? '' : Number(value) };
        }
        return { ...it, [field]: value };
      });

      if (nfType === ITEM_TYPES.PRONTA && (field === 'unitValue' || field === 'quantity')) {
        const toNum = (v) => Number(v) || 0;
        const totalExpenses =
          toNum(expenses.frete) + toNum(expenses.pedagio) +
          toNum(expenses.combustivel) + toNum(expenses.alimentacao) +
          toNum(expenses.outros);
        return recalcItemsPricing(next, nfType, totalExpenses);
      }
      return next;
    });
  };

  const handleAddItem = () => {
    const nfType = nfHeader.nfType || ITEM_TYPES.BRUTA;
    setItems((prev) => [...prev, DEFAULT_ITEM(nfType)]);
  };

  const handleRemoveItem = (localId) => {
    setItems((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((it) => it.localId !== localId);
    });
  };

  // --- CÁLCULOS TOTAIS ---
  const numericExpenses = useMemo(() => {
    const toNum = (v) => Number(v) || 0;
    return {
      frete: toNum(expenses.frete),
      pedagio: toNum(expenses.pedagio),
      combustivel: toNum(expenses.combustivel),
      alimentacao: toNum(expenses.alimentacao),
      outros: toNum(expenses.outros),
      total:
        toNum(expenses.frete) + toNum(expenses.pedagio) +
        toNum(expenses.combustivel) + toNum(expenses.alimentacao) +
        toNum(expenses.outros),
    };
  }, [expenses]);

  const subtotalItems = useMemo(() => {
    return items.reduce(
      (sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unitValue) || 0),
      0
    );
  }, [items]);

  const projectedTotal = subtotalItems + numericExpenses.total;

  // --- VALIDAÇÃO ---
  const validateForm = useCallback(() => {
    const headerErrors = {};
    const itemsErrors = {};

    if (!nfHeader.nfNumber) headerErrors.nfNumber = 'Obrigatório.';
    if (!nfHeader.supplierId) headerErrors.supplierName = 'Obrigatório.';
    if (!nfHeader.nfDate) headerErrors.nfDate = 'Obrigatório.';

    if (financialData.generateBill) {
      if (!financialData.dueDate && !financialData.isPaid) {
        setErrors(prev => ({ ...prev, general: 'Informe o Vencimento para contas a prazo.' }));
        return false;
      }
      if (!financialData.categoryId) {
        setErrors(prev => ({ ...prev, general: 'Selecione a Categoria Financeira.' }));
        return false;
      }
    }

    const nfType = nfHeader.nfType || ITEM_TYPES.BRUTA;
    if (!Array.isArray(items) || items.length === 0) {
      itemsErrors._global = 'Inclua ao menos um item na NF.';
    } else {
      items.forEach((it) => {
        const itemErr = {};
        if (!it.supplierItemName) itemErr.supplierItemName = 'Nome obrigatório.';
        // Validação da Categoria de Produto (Evita SKU GEN)
        if ((nfType === ITEM_TYPES.BRUTA || nfType === ITEM_TYPES.PRONTA) && !it.categoryId) {
          itemErr.categoryId = 'Selecione a categoria.';
        }
        if ((Number(it.quantity) || 0) <= 0) itemErr.quantity = '> 0';
        if (nfType === ITEM_TYPES.BRUTA && (Number(it.rawWeight) || 0) <= 0) itemErr.rawWeight = 'Peso obrigatório.';
        if (Object.keys(itemErr).length > 0) itemsErrors[it.localId] = itemErr;
      });
    }

    const hasHeaderErrors = Object.keys(headerErrors).length > 0;
    const hasItemErrors = Object.keys(itemsErrors).length > 0;

    if (hasHeaderErrors || hasItemErrors) {
      setErrors({ header: headerErrors, items: itemsErrors, general: 'Verifique os campos destacados.' });
      return false;
    }

    setErrors({ header: {}, items: {}, general: '' });
    return true;
  }, [nfHeader, items, financialData]);

  // --- SAVE ---
  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    setErrors((prev) => ({ ...prev, general: '' }));

    try {
      const nfType = nfHeader.nfType || ITEM_TYPES.BRUTA;
      const isPronta = nfType === ITEM_TYPES.PRONTA;

      const itemsPayload = items.map((it) => ({
        itemType: nfType,
        supplierItemCode: it.supplierItemCode, // Referência do fornecedor
        sku: it.generatedSku, // [GS-CLOUD]: SKU Canônico gerado na nuvem
        supplierItemName: it.supplierItemName,
        categoryId: it.categoryId || null,
        tags: it.tags || '',
        quantity: Number(it.quantity) || 0,
        unit: it.unit || 'un',
        rawWeight: Number(it.rawWeight) || 0,
        unitValue: Number(it.unitValue) || 0,
        // Mantemos apenas suggestedPrice para referência futura se necessário, decidiu-se remover decidedPrice
        suggestedPrice: isPronta ? Number(it.suggestedPrice) || 0 : 0,
        decidedPrice: 0
      }));

      // 1. Salvar NF e Estoque
      const result = await createNFEntryWithItemsAndLot(
        nfHeader,
        itemsPayload,
        numericExpenses,
        {}
      );

      // 2. Integração Financeira
      let financialMsg = "";
      if (financialData.generateBill) {
        // [CORREÇÃO]: Busca o objeto da categoria para pegar o NOME
        const selectedFinCat = financialCategories.find(c => c.id === financialData.categoryId);
        const categoryName = selectedFinCat?.name || 'Despesa Geral';

        const accountData = {
          description: `NF ${nfHeader.nfNumber} - ${nfHeader.supplierName}`,
          amount: projectedTotal,
          dueDate: Timestamp.fromDate(new Date(financialData.dueDate || nfHeader.nfDate)),
          issueDate: Timestamp.fromDate(new Date(nfHeader.nfDate)),
          supplierRef: doc(db, 'suppliers', nfHeader.supplierId),
          categoryRef: doc(db, 'financial_categories', financialData.categoryId),
          category: categoryName, // [CRÍTICO]: Passando o nome da categoria para evitar undefined
          status: financialData.isPaid ? 'paid' : 'pending',
          notes: `Gerado automaticamente pela entrada da NF ${nfHeader.nfNumber} / Lote ${result.lotNumber}`,
          originModule: 'nf_entry',
          originId: result.nfId
        };

        const newAccount = await addAccountPayable(accountData);

        if (financialData.isPaid) {
          await payAccount({
            accountId: newAccount.id,
            originalAccount: { ...accountData, id: newAccount.id },
            paymentData: {
              date: nfHeader.nfDate,
              amount: projectedTotal,
              method: financialData.paymentMethod,
              category: categoryName // [CRÍTICO]: Passando o nome da categoria para o pagamento
            }
          });
          financialMsg = "Despesa lançada e paga.";
        } else {
          financialMsg = "Conta a Pagar criada.";
        }
      }

      const msg = `
NF registrada: ${nfHeader.nfNumber || result.nfId}
Lote: ${result.lotNumber}
Total: R$ ${result.grandTotal.toFixed(2)}
Financeiro: ${financialMsg || 'Não gerado'}
`.trim();

      if (openModal) {
        openModal(
          <div className="space-y-2 text-sm text-gray-800">
            <p className="font-semibold text-green-600">Processo concluído com sucesso!</p>
            <pre className="bg-gray-100 p-3 rounded text-xs whitespace-pre-wrap">{msg}</pre>
          </div>,
          'Sucesso', 'sm'
        );
      } else {
        alert(msg);
      }

      // Reset
      setNfHeader({ ...nfHeader, nfNumber: '' });
      setItems([DEFAULT_ITEM(ITEM_TYPES.BRUTA)]);
      setExpenses({ frete: '', pedagio: '', combustivel: '', alimentacao: '', outros: '' });

    } catch (error) {
      console.error('Erro ao salvar:', error);
      setErrors((prev) => ({ ...prev, general: error?.message || 'Erro técnico.' }));
    } finally {
      setIsSaving(false);
    }
  };

  const isProntaNF = (nfHeader.nfType || ITEM_TYPES.BRUTA) === ITEM_TYPES.PRONTA;

  return (
    <div className="min-h-screen bg-brand-black text-white p-6">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header Página */}
        <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="flex items-center text-2xl font-bold text-white">
              <FileText className="h-7 w-7 text-brand-gold mr-2" />
              NF de Entrada (Módulo Novo)
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Fluxo completo: NF → Lote → Financeiro.
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center justify-center px-5 py-2 rounded-md bg-brand-gold text-brand-black font-semibold hover:bg-yellow-500 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salvar NF e Gerar Lote
          </button>
        </header>

        {errors.general && (
          <div className="flex items-start gap-2 bg-red-900/40 border border-red-600 text-red-100 px-4 py-3 rounded-md">
            <AlertCircle className="h-5 w-5 mt-0.5" />
            <p className="text-sm">{errors.general}</p>
          </div>
        )}

        {/* Bloco 1 — Cabeçalho */}
        <section className="bg-white text-gray-900 rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <Archive className="h-5 w-5 text-brand-gold mr-2" />
            <h2 className="text-lg font-semibold">Cabeçalho da NF</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Número da NF *</label>
              <input
                type="text"
                value={nfHeader.nfNumber}
                onChange={(e) => handleHeaderChange('nfNumber', e.target.value)}
                className={`w-full border rounded px-3 py-2 text-sm ${errors.header.nfNumber ? 'border-red-500' : 'border-gray-300 focus:border-brand-gold'}`}
              />
              {errors.header.nfNumber && <p className="text-xs text-red-600 mt-1">{errors.header.nfNumber}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Fornecedor *</label>
              <div className="flex items-center space-x-2">
                <select
                  value={nfHeader.supplierId}
                  onChange={(e) => handleSupplierSelect(e.target.value)}
                  className={`w-full border rounded px-3 py-2 text-sm ${errors.header.supplierName ? 'border-red-500' : 'border-gray-300 focus:border-brand-gold'}`}
                  disabled={isLoadingSuppliers}
                >
                  <option value="">{isLoadingSuppliers ? 'Carregando...' : 'Selecione...'}</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <button type="button" onClick={handleAddNewSupplier} className="px-2 py-2 bg-brand-gold text-brand-black rounded-md hover:bg-yellow-500">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="md:col-span-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Data da NF *</label>
              <input
                type="date"
                value={nfHeader.nfDate}
                onChange={(e) => handleHeaderChange('nfDate', e.target.value)}
                className={`w-full border rounded px-3 py-2 text-sm ${errors.header.nfDate ? 'border-red-500' : 'border-gray-300 focus:border-brand-gold'}`}
              />
            </div>

            <div className="md:col-span-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Tipo da NF *</label>
              <select
                value={nfHeader.nfType}
                onChange={(e) => handleHeaderChange('nfType', e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm border-gray-300 focus:border-brand-gold"
              >
                <option value={ITEM_TYPES.BRUTA}>BRUTA</option>
                <option value={ITEM_TYPES.PRONTA}>PRONTA</option>
                <option value={ITEM_TYPES.SERVICO}>SERVIÇO</option>
              </select>
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Observações</label>
              <textarea
                value={nfHeader.notes}
                onChange={(e) => handleHeaderChange('notes', e.target.value)}
                rows={1}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
          </div>
        </section>

        {/* Bloco 2 — Despesas */}
        <section className="bg-white text-gray-900 rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4">Despesas desta NF (Rateio)</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            {['frete', 'pedagio', 'combustivel', 'alimentacao', 'outros'].map((field) => (
              <div key={field}>
                <label className="block text-xs font-medium text-gray-600 mb-1 capitalize">{field} (R$)</label>
                <input
                  type="number" step="0.01"
                  value={expenses[field]}
                  onChange={(e) => handleExpenseChange(field, e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            ))}
          </div>
          <div className="text-sm text-gray-700">
            <p>Total de despesas: <span className="font-semibold">R$ {numericExpenses.total.toFixed(2)}</span></p>
          </div>
        </section>

        {/* Bloco 3 — Itens da NF */}
        <section className="bg-white text-gray-900 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Itens da NF</h2>
            <button type="button" onClick={handleAddItem} className="inline-flex items-center px-3 py-1.5 rounded-md bg-gray-900 text-white text-sm hover:bg-black">
              <Plus className="h-4 w-4 mr-1" /> Adicionar linha
            </button>
          </div>

          {errors.items._global && <p className="text-xs text-red-600 mb-2">{errors.items._global}</p>}

          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border border-gray-200">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  {/* [GS-CLOUD]: Nova Coluna SKU Automático */}
                  <th className="px-2 py-2 text-left w-24">SKU (Auto)</th>
                  <th className="px-2 py-2 text-left w-20">Cód. Forn.</th>
                  <th className="px-2 py-2 text-left">Nome do Item</th>
                  <th className="px-2 py-2 text-left w-32">Categoria *</th>
                  <th className="px-2 py-2 text-left w-16">Qtde</th>

                  {/* Sugestão visível apenas em PRONTA */}
                  {isProntaNF && (
                    <th className="px-2 py-2 text-left">Sugestão</th>
                  )}

                  <th className="px-2 py-2 text-left w-16">Unid.</th>
                  <th className="px-2 py-2 text-left w-20">Peso (g)</th>
                  <th className="px-2 py-2 text-left w-24">Valor Unit.</th>
                  <th className="px-2 py-2 text-left">Tags</th>
                  <th className="px-2 py-2 text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const itemErr = errors.items[it.localId] || {};
                  const nfType = nfHeader.nfType || ITEM_TYPES.BRUTA;
                  const isBruta = nfType === ITEM_TYPES.BRUTA;

                  return (
                    <tr key={it.localId} className="border-t border-gray-200">
                      {/* [GS-CLOUD]: Coluna de SKU Gerado */}
                      <td className="px-2 py-1 align-top bg-gray-50">
                        <div className="flex items-center space-x-2 h-8">
                            {it.isLoadingSku ? (
                                <Loader2 className="h-4 w-4 animate-spin text-brand-gold" />
                            ) : (
                                <div className="flex items-center text-gray-600 font-mono font-bold bg-white border px-1 rounded">
                                    {it.generatedSku || <span className="text-gray-300">-</span>}
                                </div>
                            )}
                        </div>
                      </td>

                      <td className="px-2 py-1 align-top">
                        <input
                          type="text"
                          value={it.supplierItemCode}
                          onChange={(e) => handleItemChange(it.localId, 'supplierItemCode', e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                          placeholder="Ref. Fab"
                        />
                      </td>

                      <td className="px-2 py-1 align-top">
                        <input
                          type="text"
                          value={it.supplierItemName}
                          onChange={(e) => handleItemChange(it.localId, 'supplierItemName', e.target.value)}
                          className={`w-full border rounded px-2 py-1 text-xs ${itemErr.supplierItemName ? 'border-red-500' : 'border-gray-300'}`}
                        />
                      </td>

                      <td className="px-2 py-1 align-top">
                        <select
                          value={it.categoryId}
                          onChange={(e) => handleItemChange(it.localId, 'categoryId', e.target.value)}
                          className={`w-full border rounded px-2 py-1 text-xs ${itemErr.categoryId ? 'border-red-500' : 'border-gray-300'}`}
                        >
                          <option value="">Selecione...</option>
                          {productCategories.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </td>

                      <td className="px-2 py-1 align-top">
                        <input
                          type="number"
                          min="0"
                          value={it.quantity}
                          onChange={(e) => handleItemChange(it.localId, 'quantity', e.target.value)}
                          className={`w-full border rounded px-2 py-1 text-xs ${itemErr.quantity ? 'border-red-500' : 'border-gray-300'}`}
                        />
                      </td>

                      {/* Sugestão visível apenas em PRONTA */}
                      {isProntaNF && (
                        <td className="px-2 py-1 align-top">
                          <span className="font-bold text-blue-700">
                            {formatBRL(it.suggestedPrice)}
                          </span>
                        </td>
                      )}

                      <td className="px-2 py-1 align-top">
                        <select
                          value={it.unit}
                          onChange={(e) => handleItemChange(it.localId, 'unit', e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                        >
                          <option value="un">un</option>
                          <option value="g">g</option>
                          <option value="kg">kg</option>
                        </select>
                      </td>

                      <td className="px-2 py-1 align-top">
                        <input
                          type="number"
                          step="0.01"
                          value={it.rawWeight}
                          onChange={(e) => handleItemChange(it.localId, 'rawWeight', e.target.value)}
                          className={`w-full border rounded px-2 py-1 text-xs ${itemErr.rawWeight ? 'border-red-500' : 'border-gray-300'} ${!isBruta ? 'bg-gray-100' : ''}`}
                          disabled={!isBruta}
                        />
                      </td>

                      <td className="px-2 py-1 align-top">
                        <input
                          type="number"
                          step="0.01"
                          value={it.unitValue}
                          onChange={(e) => handleItemChange(it.localId, 'unitValue', e.target.value)}
                          className={`w-full border rounded px-2 py-1 text-xs ${itemErr.unitValue ? 'border-red-500' : 'border-gray-300'}`}
                        />
                      </td>

                      <td className="px-2 py-1 align-top">
                        <input
                          type="text"
                          value={it.tags}
                          onChange={(e) => handleItemChange(it.localId, 'tags', e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                        />
                      </td>

                      <td className="px-2 py-1 align-top text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(it.localId)}
                          className="text-red-600 hover:bg-red-50 p-1 rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-800 flex justify-end gap-8 font-medium">
            <p>Itens: R$ {subtotalItems.toFixed(2)}</p>
            <p>Despesas: R$ {numericExpenses.total.toFixed(2)}</p>
            <p className="text-lg font-bold text-brand-black">Total NF: R$ {projectedTotal.toFixed(2)}</p>
          </div>
        </section>

        {/* Bloco 4 — Financeiro */}
        <section className="bg-white text-gray-900 rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between mb-4 border-b pb-2 border-gray-100">
            <div className="flex items-center gap-2">
              <DollarSign className="text-green-600" />
              <h2 className="text-lg font-semibold">Financeiro Automático</h2>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={financialData.generateBill}
                onChange={e => handleFinancialChange('generateBill', e.target.checked)}
                className="rounded text-green-600 focus:ring-green-500"
              />
              <span className="font-medium text-gray-700">Gerar Conta a Pagar</span>
            </label>
          </div>

          {financialData.generateBill && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fadeIn">

              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">Categoria Financeira *</label>
                <select
                  value={financialData.categoryId}
                  onChange={e => handleFinancialChange('categoryId', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500"
                >
                  <option value="">Selecione...</option>
                  {financialCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">Condição de Pagamento</label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="paymentCondition"
                      checked={!financialData.isPaid}
                      onChange={() => handleFinancialChange('isPaid', false)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">A Prazo</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="paymentCondition"
                      checked={financialData.isPaid}
                      onChange={() => handleFinancialChange('isPaid', true)}
                      className="text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm text-green-700 font-bold">À Vista (Baixar)</span>
                  </label>
                </div>
              </div>

              <div>
                {!financialData.isPaid ? (
                  <>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Vencimento *</label>
                    <input
                      type="date"
                      value={financialData.dueDate}
                      onChange={e => handleFinancialChange('dueDate', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500"
                    />
                  </>
                ) : (
                  <>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Forma de Pagamento</label>
                    <select
                      value={financialData.paymentMethod}
                      onChange={e => handleFinancialChange('paymentMethod', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-green-500"
                    >
                      <option value="Pix">Pix</option>
                      <option value="Dinheiro">Dinheiro</option>
                      <option value="Cartão Crédito">Cartão Crédito</option>
                      <option value="Boleto">Boleto</option>
                    </select>
                  </>
                )}
              </div>
            </div>
          )}
        </section>

      </div>
    </div>
  );
};

export default NFEntryPage;